import { useQuestionStore } from "@/stores/questionStore";

const DIFFICULTY_ORDER = ["easy", "medium", "hard"];

/**
 * Build difficulty fallback order starting from target.
 * E.g., target="medium" → ["medium", "easy", "hard"]
 */
function buildDifficultyFallback(target) {
  const t = (target || "").toLowerCase();
  if (!DIFFICULTY_ORDER.includes(t)) return DIFFICULTY_ORDER.slice();

  const idx = DIFFICULTY_ORDER.indexOf(t);
  const ordered = [DIFFICULTY_ORDER[idx]];

  // Add adjacent difficulties
  if (idx - 1 >= 0) ordered.push(DIFFICULTY_ORDER[idx - 1]);
  if (idx + 1 < DIFFICULTY_ORDER.length) ordered.push(DIFFICULTY_ORDER[idx + 1]);

  // Add any remaining
  for (const d of DIFFICULTY_ORDER) {
    if (!ordered.includes(d)) ordered.push(d);
  }

  return ordered;
}

/**
 * Question Bank composable.
 *
 * Provides cache-first question retrieval with automatic difficulty fallback.
 * Questions are prefetched by session engines (revision, diagnostic, practice)
 * and retrieved from local cache to minimize API calls.
 *
 * @example
 * // In session engine:
 * const questionStore = useQuestionStore();
 * const questionBank = useQuestionBank();
 *
 * // 1. Prefetch at session start (1 API call)
 * await questionStore.prefetch(['WS_ADD', 'WS_SUB'], { perGroup: 15 });
 *
 * // 2. Get questions during session (from cache, no API calls)
 * const q1 = await questionBank.getQuestion('WS_ADD', 'medium');
 * const q2 = await questionBank.getQuestion('WS_SUB', 'hard');
 */
export function useQuestionBank() {
  const questionStore = useQuestionStore();

  /**
   * Get one question with cache-first strategy and difficulty fallback.
   *
   * Strategy:
   * 1. Try each difficulty in fallback order from cache
   * 2. If cache miss and not cacheOnly, lazy-prefetch that section group
   * 3. Try cache again
   * 4. If still empty and not cacheOnly, fetch single question from API (last resort)
   *
   * @param {string} sectionGroupId - Section group to fetch from
   * @param {string} [targetDifficulty='medium'] - Preferred difficulty
   * @param {Object} [options]
   * @param {boolean} [options.cacheOnly=false] - If true, only read from cache (no API calls)
   * @returns {Promise<Object|null>} Single question or null if none found
   */
  async function getQuestion(sectionGroupId, targetDifficulty = "medium", { cacheOnly = false } = {}) {
    const difficultiesToTry = buildDifficultyFallback(targetDifficulty);

    // 1. Try cache first (no API call)
    for (const diff of difficultiesToTry) {
      const cached = questionStore.getFromCache(sectionGroupId, diff);
      if (cached) return cached;
    }

    if (cacheOnly) return null;

    // 2. Cache miss - check if we need to prefetch
    if (!questionStore.isPrefetched(sectionGroupId)) {
      console.warn(`QuestionBank: cache miss for ${sectionGroupId}, prefetching...`);
      await questionStore.prefetch([sectionGroupId], { perGroup: 15 });

      // 3. Try cache again after prefetch
      for (const diff of difficultiesToTry) {
        const cached = questionStore.getFromCache(sectionGroupId, diff);
        if (cached) return cached;
      }
    }

    // 4. Still no luck - try fetching single question (last resort)
    console.warn(`QuestionBank: cache exhausted for ${sectionGroupId}, fetching single...`);

    // Try with target difficulty first
    let question = await questionStore.fetchSingle(sectionGroupId, targetDifficulty);
    if (question) return question;

    // Try without difficulty filter
    question = await questionStore.fetchSingle(sectionGroupId, null);
    if (question) return question;

    // Truly exhausted
    console.warn(`QuestionBank: no questions available for ${sectionGroupId}`);
    return null;
  }

  /**
   * Prefetch questions for multiple section groups.
   * Call this before starting a session.
   *
   * @param {string[]} sectionGroupIds - Section groups to prefetch
   * @param {Object} [options]
   * @param {number} [options.perGroup=15] - Questions per section group
   * @returns {Promise<void>}
   */
  async function prefetch(sectionGroupIds, options = {}) {
    return questionStore.prefetch(sectionGroupIds, options);
  }

  /**
   * Reset for new session (clears used question tracking).
   */
  function resetSession() {
    questionStore.resetSession();
  }

  /**
   * Get available question count for debugging/UI
   */
  function getAvailableCount(sectionGroupId, difficulty) {
    return questionStore.getAvailableCount(sectionGroupId, difficulty);
  }

  return {
    getQuestion,
    prefetch,
    resetSession,
    getAvailableCount
  };
}
